import { Box, Stack, Tooltip, Typography } from '@mui/material';

import {
  formatLicensePlateLabel,
  getNormalizedLicensePlates,
} from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import { IMasterVehicle } from '@/models';

interface MasterVehiclePlateCellProps {
  vehicle: Pick<IMasterVehicle, 'licensePlate' | 'licensePlates'>;
}

const tooltipContent = (
  plateNumbers: ReturnType<typeof getNormalizedLicensePlates>,
) => (
  <Stack spacing={0.375} sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
    {plateNumbers.map((plate, index) => (
      <Box
        key={`${plate.number}-${index}`}
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          columnGap: 1,
          alignItems: 'center',
        }}
      >
        <Typography
          component="span"
          sx={{
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '17px',
            whiteSpace: 'nowrap',
          }}
        >
          {formatLicensePlateLabel(plate)}
        </Typography>
        {plate.isActive && (
          <Typography
            component="span"
            sx={{
              color: '#64D0AE',
              fontSize: 11,
              fontWeight: 600,
              lineHeight: '17px',
              whiteSpace: 'nowrap',
            }}
          >
            Active
          </Typography>
        )}
      </Box>
    ))}
  </Stack>
);

export const MasterVehiclePlateCell = ({
  vehicle,
}: MasterVehiclePlateCellProps) => {
  const plateNumbers = [...getNormalizedLicensePlates(vehicle)].sort(
    (a, b) => Number(Boolean(b.isActive)) - Number(Boolean(a.isActive)),
  );
  const activePlate = plateNumbers.find((plate) => plate.isActive);
  const additionalCount = Math.max(plateNumbers.length - 1, 0);

  if (!activePlate) {
    return <>/</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent !important',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          lineHeight: 1.25,
          backgroundColor: 'transparent !important',
        }}
      >
        <Typography
          variant="body2"
          component="span"
          sx={{ lineHeight: 1.25, backgroundColor: 'transparent !important' }}
        >
          {formatLicensePlateLabel(activePlate)}
        </Typography>
      </Box>
      {additionalCount > 0 && (
        <Tooltip
          title={tooltipContent(plateNumbers)}
          arrow
          placement="bottom"
          slotProps={{
            tooltip: {
              sx: {
                minWidth: 138,
                maxWidth: 'none',
                borderRadius: '6px',
                py: '9px',
                px: '12px',
                bgcolor: '#0E3145',
                boxShadow: '0px 4px 12px rgba(14, 49, 69, 0.24)',
                overflow: 'visible',
              },
            },
            arrow: {
              sx: {
                color: '#0E3145',
              },
            },
            popper: {
              disablePortal: false,
              popperOptions: {
                strategy: 'fixed',
              },
              sx: {
                zIndex: 1600,
              },
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [2, 7],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'window',
                  },
                },
              ],
            },
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 22,
              height: 16,
              px: 0.5,
              py: 0,
              borderRadius: '8px',
              backgroundColor: '#E7EAEC',
              color: '#0E3145',
              fontSize: 10,
              fontWeight: 500,
              lineHeight: '14px',
              cursor: 'pointer',
            }}
          >
            +{additionalCount}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
